import React, { useRef } from "react";
import html2pdf from "html2pdf.js";
import { X, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import { formatBRDate } from "../utils/date";
import { stripTrailingNumber } from "../utils/lodgeName";

/**
 * Emite um certificado formal em PDF para Maçom Emérito ou Provecto.
 * Layout paisagem A4, moldura dourada/verde conforme o título, tipografia serifada.
 */
export const HonorCertificate = ({ isOpen, onClose, member, lodgeInfo }) => {
  const printRef = useRef(null);

  if (!isOpen || !member) return null;

  const isEmerito = member.honor_title === "emerito";
  const isProvecto = member.honor_title === "provecto";
  if (!isEmerito && !isProvecto) return null;

  const titleName = isEmerito ? "MAÇOM EMÉRITO" : "MAÇOM PROVECTO";
  const primaryColor = isEmerito ? "#B8860B" : "#0F766E";
  const primarySoft = isEmerito ? "#F4E4B8" : "#D1FAE5";
  const primaryStrong = isEmerito ? "#8B6508" : "#065F46";

  const lodgeName = stripTrailingNumber(lodgeInfo?.name) || "Loja Maçônica";
  const lodgeNumber = lodgeInfo?.number || "";
  const lodgeOriente = lodgeInfo?.oriente || "Oriente";
  const grantedDate = member.honor_title_granted_at
    ? formatBRDate(member.honor_title_granted_at)
    : "—";

  const todayBR = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit", month: "long", year: "numeric"
  });

  const handleDownload = async () => {
    const src = printRef.current;
    if (!src) return;
    const wrapper = document.createElement("div");
    wrapper.style.position = "fixed";
    wrapper.style.left = "-10000px";
    wrapper.style.top = "0";
    wrapper.style.zIndex = "-1";

    const clone = src.cloneNode(true);
    clone.style.cssText = "position: static; width: 1123px; min-height: 794px; background: #ffffff; opacity: 1; visibility: visible;";
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    toast.message("Gerando certificado...");
    try {
      await html2pdf()
        .set({
          margin: 0,
          filename: `Certificado_${titleName.replace(/\s+/g, "_")}_${(member.name || "").replace(/\s+/g, "_")}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false, width: 1123, windowWidth: 1123 },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        })
        .from(clone)
        .save();
      toast.success("Certificado gerado! Pronto para impressão.");
    } catch (err) {
      toast.error("Falha ao gerar PDF: " + (err?.message || "erro"));
    } finally {
      if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto">
      <div
        data-testid="honor-certificate-modal"
        className="w-full max-w-6xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-950">
          <div>
            <h3 className="font-serif font-bold text-lg text-white">
              Certificado de {titleName.toLowerCase().replace("maçom ", "Maçom ")}
            </h3>
            <p className="text-xs text-slate-400">Pré-visualização — pronto para impressão em papel A4 paisagem</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="btn-download-certificate"
              onClick={handleDownload}
              className="flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2 rounded-xl text-xs uppercase tracking-wider"
            >
              <Download className="w-4 h-4" /> Baixar PDF
            </button>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-3 py-2 rounded-xl text-xs uppercase"
            >
              <Printer className="w-4 h-4" /> Imprimir
            </button>
            <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-8 overflow-y-auto bg-slate-950">
          {/* CERTIFICATE CANVAS */}
          <div
            ref={printRef}
            id="honor-certificate-print"
            style={{
              width: "1123px",
              minHeight: "794px",
              margin: "0 auto",
              background: "#FFFDF7",
              color: "#111",
              fontFamily: "Georgia, 'Times New Roman', serif",
              position: "relative",
              padding: "50px 60px",
              boxSizing: "border-box",
              border: `14px double ${primaryColor}`,
              boxShadow: "0 8px 30px rgba(0,0,0,0.35)"
            }}
          >
            {/* Inner ornamental frame */}
            <div style={{
              border: `2px solid ${primaryColor}`,
              padding: "28px 36px",
              minHeight: "670px",
              position: "relative",
              background: `radial-gradient(ellipse at top, ${primarySoft}22 0%, transparent 60%)`
            }}>
              {/* Corner ornaments */}
              {["top-left", "top-right", "bottom-left", "bottom-right"].map((c) => (
                <div key={c} style={{
                  position: "absolute",
                  width: "36px",
                  height: "36px",
                  border: `3px solid ${primaryColor}`,
                  ...(c.includes("top") ? { top: "-8px" } : { bottom: "-8px" }),
                  ...(c.includes("left") ? { left: "-8px", borderRight: "none", borderBottom: c.includes("top") ? "none" : `3px solid ${primaryColor}` } : { right: "-8px", borderLeft: "none", borderBottom: c.includes("top") ? "none" : `3px solid ${primaryColor}` }),
                  ...(c.includes("bottom") ? { borderTop: "none" } : {}),
                }} />
              ))}

              {/* Header — À Glória do Grande Arquiteto */}
              <div style={{ textAlign: "center", marginBottom: "18px" }}>
                <div style={{
                  display: "inline-block",
                  padding: "6px 26px",
                  background: primaryColor,
                  color: "#FFFDF7",
                  fontSize: "13px",
                  letterSpacing: "6px",
                  fontWeight: 700,
                  borderRadius: "2px"
                }}>
                  À GLÓRIA DO GRANDE ARQUITETO DO UNIVERSO
                </div>
              </div>

              {/* Lodge name */}
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <div style={{ fontSize: "22px", fontWeight: 700, color: primaryStrong, letterSpacing: "1px" }}>
                  {lodgeName}{lodgeNumber ? ` nº ${lodgeNumber}` : ""}
                </div>
                <div style={{ fontSize: "13px", color: "#555", fontStyle: "italic", marginTop: "4px" }}>
                  Oriente de {lodgeOriente}
                </div>
              </div>

              {/* Divider ornament */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginBottom: "24px" }}>
                <div style={{ flex: 1, maxWidth: "180px", height: "1px", background: primaryColor }} />
                <div style={{ fontSize: "22px", color: primaryColor }}>✦</div>
                <div style={{ flex: 1, maxWidth: "180px", height: "1px", background: primaryColor }} />
              </div>

              {/* Title */}
              <div style={{ textAlign: "center", marginBottom: "26px" }}>
                <div style={{ fontSize: "18px", color: "#333", letterSpacing: "3px", marginBottom: "6px" }}>
                  CERTIFICADO DE CONCESSÃO DO TÍTULO DE
                </div>
                <div style={{
                  fontSize: "48px",
                  fontWeight: 900,
                  color: primaryColor,
                  letterSpacing: "8px",
                  textShadow: "1px 1px 0 rgba(0,0,0,0.08)",
                  fontFamily: "'Trajan Pro', Georgia, serif"
                }}>
                  {titleName}
                </div>
              </div>

              {/* Body */}
              <div style={{ fontSize: "15px", textAlign: "justify", lineHeight: 1.9, color: "#222", padding: "0 20px" }}>
                <p style={{ margin: "0 0 14px" }}>
                  Nós, os Obreiros da Augusta e Respeitável Loja Simbólica <b>{lodgeName}{lodgeNumber ? ` nº ${lodgeNumber}` : ""}</b>, reunidos em Sessão Magna
                  no Vale do Oriente de <b>{lodgeOriente}</b>, no uso de nossas prerrogativas e por unânime deliberação
                  do Quadro de Obreiros, temos a honra de conceder ao dileto Irmão
                </p>

                <p style={{
                  textAlign: "center",
                  fontSize: "30px",
                  fontWeight: 700,
                  fontFamily: "Georgia, serif",
                  color: primaryStrong,
                  margin: "18px 0 10px",
                  padding: "6px 0",
                  borderTop: `1px solid ${primaryColor}55`,
                  borderBottom: `1px solid ${primaryColor}55`
                }}>
                  {member.name}
                </p>

                <p style={{ textAlign: "center", fontSize: "12px", color: "#555", marginBottom: "18px" }}>
                  CIM {member.cim} &nbsp;•&nbsp; Grau: {member.degree}
                </p>

                <p style={{ margin: "0 0 14px" }}>
                  O honroso título de <b>{titleName}</b>, em reconhecimento aos relevantes serviços prestados à Ordem,
                  à sua constância no cumprimento dos deveres maçônicos, ao seu exemplo de conduta e à sua notável
                  dedicação aos princípios da Fraternidade, da Sabedoria e da Virtude.
                </p>

                <p style={{ margin: "0 0 14px" }}>
                  Este título vitalício foi outorgado em <b>{grantedDate}</b>, na presença dos Irmãos do Quadro,
                  ficando o Irmão homenageado credor de todas as honrarias, dignidades e prerrogativas inerentes
                  a esta elevação, na conformidade dos usos e costumes da Sublime Ordem Maçônica.
                </p>
              </div>

              {/* Footer signatures */}
              <div style={{ marginTop: "56px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px", padding: "0 20px" }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ borderTop: `1.5px solid ${primaryColor}`, paddingTop: "6px", fontSize: "13px", fontWeight: 700, color: "#222" }}>
                    Venerável Mestre
                  </div>
                  <div style={{ fontSize: "11px", color: "#666", fontStyle: "italic" }}>Presidente da Sessão</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ borderTop: `1.5px solid ${primaryColor}`, paddingTop: "6px", fontSize: "13px", fontWeight: 700, color: "#222" }}>
                    Secretário
                  </div>
                  <div style={{ fontSize: "11px", color: "#666", fontStyle: "italic" }}>Guardião das Atas</div>
                </div>
              </div>

              {/* Date issued */}
              <div style={{
                textAlign: "center",
                marginTop: "28px",
                fontSize: "12px",
                color: "#555",
                fontStyle: "italic"
              }}>
                Emitido em {todayBR}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
